import React from 'react';
import { Box, Collapse, Typography, alpha, useTheme } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import {
  getFileIcon,
  getFileTypeDisplay,
} from '@/common/utils/fileDisplayUtils';
import { formatFileSize, getFileCategory } from '@/common/utils/fileUtils';
import { AudioPlayer } from './AudioPlayer';
import { FileCategory } from '@/common/models/enums';
import { WaveForm } from '@/generated/graphql';
import type { FileNode } from '@/common/utils/fileTreeUtils';

interface MobileFileExplorerProps {
  fileTree: FileNode[];
  projectId: string;
  expandedFolders: Set<string>;
  toggleFolder: (path: string) => void;
}

const MobileFileExplorer: React.FC<MobileFileExplorerProps> = ({
  fileTree,
  projectId,
  expandedFolders,
  toggleFolder,
}) => {
  const theme = useTheme();

  const renderNode = (node: FileNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.path);
    const pl = 16 + depth * 20;

    if (node.type === 'folder') {
      return (
        <React.Fragment key={node.path}>
          <Box
            data-cy={`folder-name-${node.name}`}
            onClick={() => toggleFolder(node.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 48,
              pl: `${pl}px`,
              pr: 1.5,
              gap: 1,
              cursor: 'pointer',
              backgroundColor: isExpanded
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.common.white, 0.04),
              borderLeft: `3px solid ${
                isExpanded ? theme.palette.primary.main : 'transparent'
              }`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              userSelect: 'none',
              '&:active': {
                backgroundColor: alpha(theme.palette.primary.main, 0.18),
              },
            }}
          >
            {isExpanded ? (
              <FolderOpenIcon
                sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }}
              />
            ) : (
              <FolderIcon
                sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }}
              />
            )}
            <Typography
              sx={{
                flex: 1,
                fontSize: '14px',
                fontWeight: 500,
                color: isExpanded ? 'primary.main' : 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node.name}
            </Typography>
            {isExpanded ? (
              <ExpandMoreIcon
                sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
              />
            ) : (
              <ChevronRightIcon
                sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
              />
            )}
          </Box>

          <Collapse in={isExpanded} timeout={150} unmountOnExit>
            {node.children?.map((child) => renderNode(child, depth + 1))}
          </Collapse>
        </React.Fragment>
      );
    }

    const isAudio =
      getFileCategory(node.contentType || '', node.name || '') ===
      FileCategory.AUDIO;
    const hasPlayer = isAudio && node.waveForm;

    return (
      <Box
        key={node.path}
        data-cy={`file-name-${node.name}`}
        sx={{
          pl: `${pl}px`,
          pr: 2,
          pt: 1.25,
          pb: hasPlayer ? 1.5 : 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Icon + name + metadata */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              width: 20,
            }}
          >
            {getFileIcon(node.name, node.contentType || '')}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.35,
              }}
            >
              {node.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                mt: 0.25,
                lineHeight: 1.2,
              }}
            >
              {getFileTypeDisplay(node.name, node.contentType || '')}
              {node.size ? ` · ${formatFileSize(node.size)}` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Audio player */}
        {hasPlayer && (
          <Box sx={{ mt: 1 }}>
            <AudioPlayer
              waveForm={node.waveForm as WaveForm}
              projectId={projectId}
              fileId={node._id}
            />
          </Box>
        )}
      </Box>
    );
  };

  return <Box>{fileTree.map((node) => renderNode(node))}</Box>;
};

export default MobileFileExplorer;
